// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import HeaderClickable, {
    HeaderClickableProps,
    HeaderClickableStyles,
} from "@west/App/Components/Header/HeaderClickable";
import MenuTippy from "@west/App/Components/Tippy/MenuTippy";
import { CustomAppState, xGoogleAnalyticsState, xGoogleAnalyticsStateType } from "@west/App/Store/Reducers";
import { XNGAGE_CUSTOM_WIDGET } from "@west/XDL/constants";
import Help from "@west/XDL/Icons/Help";
import { xScreen } from "@west/XDL/Utilities";
import { openHubSpotLiveChatHandler } from "@west/XDL/Utilities/liveChat";
import { getDataLayer, googleAnalyticsNeedHelpClickHandler } from "@west/XDL/Utilities/tracking";
import xDefaultRules from "@west/XDL/Utilities/xValidate/xDefaultRules";
import validate from "@west/XDL/Utilities/xValidate/xValidate";
import parse from "html-react-parser";
import React, { FC, useState } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    destination = "destination",
    description = "description",
    email = "email",
    phoneCall = "phoneCall",
    phoneText = "phoneText",
    supportLinks = "supportLinks",
}

export interface NeedHelpComponentProps {
    fields: {
        [fields.destination]: LinkFieldValue;
        [fields.description]: string;
        [fields.email]: string;
        [fields.phoneCall]: string;
        [fields.phoneText]: string;
        [fields.supportLinks]: LinkModel[];
    };
    xGoogleAnalytics?: xGoogleAnalyticsStateType;
}

interface OwnProps extends NeedHelpComponentProps {}

interface NeedHelpComponentStyles {
    container?: InjectableCss;
    titleContainer?: InjectableCss;
    title?: TypographyPresentationProps;
    contactUsContainer?: InjectableCss;
    contactUsLinkWrapper?: InjectableCss;
    contactUsDescription?: TypographyPresentationProps;
    liveChat?: LinkPresentationProps;
    email?: LinkPresentationProps;
    phoneCall?: LinkPresentationProps;
    phoneText?: LinkPresentationProps;
    supportLinksContainer?: InjectableCss;
    supportLinkWrapper?: InjectableCss;
    supportLink?: LinkPresentationProps;
}

interface NeedHelpStyles {
    component?: NeedHelpComponentStyles;
    headerClickableStyles?: HeaderClickableStyles;
}

export const needHelpStyles: NeedHelpStyles = {};

const styles = needHelpStyles;

const NeedHelpComponent: FC<NeedHelpComponentProps> = ({ fields, xGoogleAnalytics }) => {
    const componentStyles = styles.component || {};
    const supportLinks = useGetLinks(fields.supportLinks, o => o.fields.destination);

    return (
        <StyledWrapper {...componentStyles.container}>
            <StyledWrapper {...componentStyles.titleContainer}>
                <Typography {...componentStyles.title}>{translate("Contact Us")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...componentStyles.contactUsContainer}>
                {fields.description && (
                    <Typography {...componentStyles.contactUsDescription} as="div">
                        {parse(fields.description, parserOptions)}
                    </Typography>
                )}
                <StyledWrapper {...componentStyles.contactUsLinkWrapper}>
                    <Link {...componentStyles.liveChat} onClick={openHubSpotLiveChatHandler}>
                        {translate("Live Chat")}
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...componentStyles.contactUsLinkWrapper}>
                    {fields.email && (
                        <Link
                            {...componentStyles.email}
                            href={`mailTo:${fields.email}`}
                            onClick={() =>
                                googleAnalyticsNeedHelpClickHandler(
                                    xGoogleAnalytics,
                                    `mailTo:${fields.email}`,
                                    fields.email,
                                )
                            }
                        >
                            {fields.email}
                        </Link>
                    )}
                </StyledWrapper>
                <StyledWrapper {...componentStyles.contactUsLinkWrapper}>
                    {fields.phoneCall && (
                        <Link
                            {...componentStyles.phoneCall}
                            href={`tel:${fields.phoneCall}`}
                            aria-label={translate("Call")}
                            onClick={() =>
                                googleAnalyticsNeedHelpClickHandler(
                                    xGoogleAnalytics,
                                    `tel:${fields.phoneCall}`,
                                    translate("Call"),
                                )
                            }
                        >
                            {translate("Call")}
                            <Typography>{fields.phoneCall}</Typography>
                        </Link>
                    )}
                </StyledWrapper>
                <StyledWrapper {...componentStyles.contactUsLinkWrapper}>
                    {fields.phoneText && (
                        <Link
                            {...componentStyles.phoneText}
                            href={`tel:${fields.phoneText}`}
                            onClick={() =>
                                googleAnalyticsNeedHelpClickHandler(
                                    xGoogleAnalytics,
                                    `tel:${fields.phoneText}`,
                                    translate("Text"),
                                )
                            }
                        >
                            {translate("Text")}
                            <Typography>{fields.phoneText}</Typography>
                        </Link>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...componentStyles.titleContainer}>
                <Typography {...componentStyles.title}>{translate("Support")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...componentStyles.supportLinksContainer}>
                {supportLinks.map(
                    (link, index) =>
                        link.url && (
                            <StyledWrapper
                                {...componentStyles.supportLinkWrapper}
                                key={fields.supportLinks[index].fields.overriddenTitle || link.title}
                            >
                                <Link
                                    {...componentStyles.supportLink}
                                    href={link.url}
                                    target={fields.supportLinks[index].fields.openInNewWindow ? "_blank" : ""}
                                    onClick={() =>
                                        googleAnalyticsNeedHelpClickHandler(
                                            xGoogleAnalytics,
                                            link.url,
                                            fields.supportLinks[index].fields.overriddenTitle || link.title,
                                        )
                                    }
                                >
                                    {fields.supportLinks[index].fields.overriddenTitle || link.title}
                                </Link>
                            </StyledWrapper>
                        ),
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const NeedHelp: FC<OwnProps & WidgetProps> = props => {
    const destinationLink = useGetLink(props.fields.destination);
    const [isMenuTippyOpen, setIsMenuTippyOpen] = useState(false);
    const dispatch = useDispatch();
    const { isTabletScreen, xGoogleAnalytics } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        // Google Analytics
        xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
    }));

    const openDrawer = () => {
        dispatch(setNavDrawerIsOpen({ navDrawerIsOpen: true }));
        dispatch(setInitialValues({}));
        // XNG-changes: open need help drawer when clicking on needHelp clickable in headercofr tablet/mobile
        setTimeout(() => {
            const element = document.getElementById("NeedHelpNavigationDrawer");
            const button = element?.getElementsByClassName("main-navigation-row")?.[0] as HTMLButtonElement;
            if (button) {
                button?.click();
            }
        }, 500);
    };

    const message = translate("Need Help?");

    if (isTabletScreen) {
        return (
            <HeaderClickable
                onClick={openDrawer}
                aria-label={message}
                href={destinationLink.url}
                variant="menu"
                extendedStyles={styles.headerClickableStyles}
            >
                {message}
            </HeaderClickable>
        );
    }

    return (
        <MenuTippy
            maxWidth={432}
            content={<NeedHelpComponent {...props} xGoogleAnalytics={xGoogleAnalytics} />}
            onShowTrigger={isOpen => setIsMenuTippyOpen(isOpen)}
        >
            <HeaderClickable
                isOpen={isMenuTippyOpen}
                aria-label={message}
                href={destinationLink.url}
                variant="menu"
                extendedStyles={styles.headerClickableStyles}
            >
                {message}
            </HeaderClickable>
        </MenuTippy>
    );
};

const contactUsTab = {
    displayName: "Contact Us",
    sortOrder: 1,
};

const supportTab = {
    displayName: "Support",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: NeedHelp,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
        fieldDefinitions: [
            {
                name: fields.destination,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "Translatable",
                allowCategories: item => false,
                tab: contactUsTab,
            },
            {
                name: fields.description,
                fieldType: "Translatable",
                defaultValue: "",
                editorTemplate: "RichTextField",
                tab: contactUsTab,
            },
            {
                name: fields.email,
                fieldType: "Translatable",
                defaultValue: "",
                editorTemplate: "TextField",
                validate: value => {
                    if (!value) {
                        return null;
                    }

                    return validate(value, xDefaultRules["email"]);
                },
                tab: contactUsTab,
            },
            {
                name: fields.phoneCall,
                fieldType: "Translatable",
                defaultValue: "",
                editorTemplate: "TextField",
                validate: value => {
                    if (!value) {
                        return null;
                    }

                    return validate(value, xDefaultRules["phone"]);
                },
                tab: contactUsTab,
            },
            {
                name: fields.phoneText,
                fieldType: "Translatable",
                defaultValue: "",
                editorTemplate: "TextField",
                validate: value => {
                    if (!value) {
                        return null;
                    }

                    return validate(value, xDefaultRules["phone"]);
                },
                tab: contactUsTab,
            },
            {
                name: fields.supportLinks,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        allowCategories: item => false,
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: supportTab,
            },
        ],
    },
};

export default widgetModule;
export { NeedHelpComponent };
